<template>
    <v-container fluid class="pa-3" v-if="sessionActive">

        <v-alert v-if="!hasPermission(minPermission)"
                 class="body-2"
                 type="error"
        >
            Your user account doesn't have sufficient permissions to use this feature
        </v-alert>

        <v-card flat v-if="hasPermission(minPermission)">

            <v-container fluid>
                <v-row no-gutters>
                    <v-col cols="12">
                        <v-alert class="body-2" v-show="available > 100" type="info">
                            You have {{ available }} redemptions that need approval. We are showing the first 100. When you are
                            done with this batch hit the reload button to get the next batch.
                        </v-alert>
                    </v-col>
                </v-row>

                <v-row dense>

                    <v-scale-transition>
                        <v-col cols="4" xl="2" lg="3" md="4" sm="6" xs="12" v-show="redemptionsToReviewCount > 0">
                            <stat-box-numerical hint="Redemptions in this batch"
                                                hint-at-top
                                                :loading="loading"
                                                color="primary"
                                                :value="redemptionsToReviewCount"
                                                :mouseOverValue="available"
                                                mouseOverHint="Total redemptions to go..."
                            />
                        </v-col>
                    </v-scale-transition>

                    <v-scale-transition>
                        <v-col cols="4" xl="2" lg="3" md="4" sm="6" xs="12" v-show="redemptionsApprovedCount > 0">
                            <stat-box-numerical hint="Approved"
                                                hint-at-top
                                                :loading="loading"
                                                :color="approvedColor"
                                                :value="redemptionsApprovedCount"
                            />
                        </v-col>
                    </v-scale-transition>

                    <v-col cols="4" xl="2" lg="3" md="4" sm="6" xs="12" v-show="redemptionsRejectedCount > 0">
                        <stat-box-numerical hint="Rejected"
                                            hint-at-top
                                            :loading="loading"
                                            :color="rejectedColor"
                                            :value="redemptionsRejectedCount"
                        />
                    </v-col>
                    <v-spacer></v-spacer>

                    <v-col cols="1" class="text-right">
                        <!-- Reload -->
                        <v-tooltip top color="primary" v-if="clearApprovedButtonVisible">
                            <template v-slot:activator="{ on }">
                                <v-btn class="mt-12"
                                       @click="getApprovals(true, false)"
                                       v-on="on"
                                       color="primary"
                                       small>
                                    Clear processed
                                </v-btn>
                            </template>
                            <span>Clear approved or rejected items from the screen</span>
                        </v-tooltip>
                    </v-col>

                    <v-col cols="1" class="text-right">
                        <!-- Reload -->
                        <v-tooltip top color="primary">
                            <template v-slot:activator="{ on }">
                                <v-btn class="mt-12"
                                       @click="getApprovals(true, true)"
                                       v-on="on"
                                       icon>
                                    <v-icon>mdi-refresh</v-icon>
                                </v-btn>
                            </template>
                            <span>Reload data</span>
                        </v-tooltip>
                    </v-col>

                </v-row>
            </v-container>

        </v-card>

        <v-row v-if="hasPermission(minPermission)">

            <v-col cols="12" class="pt-4">

                <v-data-table
                    :headers="headers"
                    :items="redemptions"
                    :loading="loading"
                    :show-select="hasPermission('approve redemptions')"
                    @toggle-select-all="selectAll"
                    :footer-props="{itemsPerPageOptions: [10,25,50,100]}"
                >
                    <template v-slot:body="{ items }">
                        <tbody>

                        <!-- NO DATA INFO -->
                        <tr v-if="items.length===0 && !loading">
                            <td colspan="99">
                                <v-alert
                                    color="primary"
                                    class="mt-4 body-2"
                                    icon="mdi-information">
                                    No redemptions found
                                </v-alert>
                            </td>
                        </tr>

                        <tr v-for="item in items" :key="item.id">

                            <!-- CHECKBOX -->
                            <td>
                                <v-checkbox color="primary"
                                            :disabled="!hasPermission('approve redemptions') && item.approved !== null"
                                            class="mt-0"
                                            hide-details
                                            v-model="selected"
                                            :value="item.id"></v-checkbox>
                            </td>

                            <!-- REDEMPTION ID -->
                            <td class="text-center">
                                <v-chip small>
                                    {{ item.id }}
                                </v-chip>
                            </td>

                            <!-- COST OF REDEMPTION -->
                            <td>
                                <div v-if="item.reward">{{ item.reward.name }}</div>
                                <div v-else>
                                    <v-tooltip top color="primary" v-if="hasPermission('view projects')">
                                        <template v-slot:activator="{ on }">
                                            <v-icon small>mdi-circle-off-outline</v-icon>
                                        </template>
                                        <span>No reward associated to this redemptions</span>
                                    </v-tooltip>
                                </div>
                            </td>

                            <!-- COST -->
                            <td class="text-right">
                                {{ item.cost.toLocaleString(undefined, {minimumFractionDigits: 2}) }} {{ item.cost_currency }}
                            </td>

                            <!-- APPROVED -->
                            <td>
                                <v-chip label :color="rejectedColor" v-if="item.approved === false">
                                    <v-icon left>mdi-close</v-icon>
                                    Rejected
                                </v-chip>
                                <v-chip label :color="approvedColor" v-if="item.approved === true">
                                    <v-icon left>mdi-check</v-icon>
                                    Approved
                                </v-chip>
                                <v-chip label :color="reviewColor" v-if="item.approved === null">
                                    <v-icon left>mdi-eye-outline</v-icon>
                                    Review
                                </v-chip>
                            </td>

                            <!-- PANELIST -->
                            <td class="text-no-wrap text-center">
                                <v-tooltip top color="primary" v-if="hasPermission('view projects')">
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on"
                                               fab
                                               class="elevation-0"
                                               x-small
                                               @click="panelistClicked(item.panelist_id)"
                                               color="primary">
                                            <v-icon small>mdi-card-account-details-outline</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>View panelist dashboard</span>
                                </v-tooltip>
                            </td>

                            <!-- WHEN -->
                            <td>
                                {{ toHumanFrom(item.updated_at) }}
                            </td>
                        </tr>

                        </tbody>
                    </template>

                </v-data-table>

                <v-card flat v-if="hasPermission('approve redemptions')">
                    <v-card-text class="pt-0 pb-0">
                        <v-btn :color="approvedColor"
                               :loading="approving"
                               :disabled="rejecting || selected.length === 0"
                               @click="approveSelected"
                        >Approve selected
                        </v-btn>
                        <v-btn class="ml-2"
                               :color="rejectedColor"
                               :loading="rejecting"
                               :disabled="approving || selected.length === 0"
                               @click="rejectSelected"
                        >Reject selected
                        </v-btn>
                    </v-card-text>
                </v-card>

            </v-col>

        </v-row>
    </v-container>
</template>

<script>

import Json from 'vue-json-pretty';
import Store from '../store';
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';
import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
import MaterialHeader from '../../../components/UI/MaterialHeader';
import {timeDisplay} from "../../../components/mixins/timeDisplayMixin";
import Avatar from "../../../components/UI/Avatar";
import StatBoxNumerical from "../../../components/UI/StatBoxNumerical";

const vuexModule = 'Redemptions';

export default {

    mixins: [activeSessionChecker, timeDisplay],

    components: {
        StatBoxNumerical,
        Json,
        MaterialHeader,
        Avatar,
    },

    data() {
        return {
            minPermission: 'view redemptions',
            selected: [],
            approvedColor: 'green darken-2',
            rejectedColor: 'red darken-2',
            reviewColor: 'primary',
            headers: [
                {text: 'ID', align: 'center ', sortable: true, value: 'id', width: '40px'},
                {text: 'REWARD', align: 'left', sortable: false, width: '200px', value: 'reward.name'},
                {text: 'COST', align: 'right', value: 'cost', width: '80px'},
                {text: 'STATUS', value: 'approved', width: '70px'},
                {text: 'PANELIST', align: 'center', sortable: false, width: '50px', value: 'panelist_id'},
                {text: 'CALENDAR', sortable: false, align: 'left', width: '180px'},
            ],
        }
    },
    computed: {

        ...mapState(vuexModule, [
            'redemptions',
            'available',
            'loading',
            'approving',
            'rejecting'
        ]),

        redemptionsToReviewCount() {
            return this.redemptions.filter(redemption => redemption.approved === null).length;
        },

        redemptionsApprovedCount() {
            return this.redemptions.filter(redemption => redemption.approved === true).length;
        },

        redemptionsRejectedCount() {
            return this.redemptions.filter(redemption => redemption.approved === false).length;
        },

        clearApprovedButtonVisible() {
            return this.redemptionsApprovedCount + this.redemptionsRejectedCount > 0;
        },

        ...mapGetters(vuexModule, []),
    },

    methods: {
        ...mapActions(vuexModule, [
            'loadRedemptionApprovals',
            'approveRedemptions',
            'rejectRedemptions'
        ]),

        ...mapMutations(vuexModule, []),

        selectAll(config) {
            if (config.value) {
                this.selected = this.redemptions.map(redemption => redemption.id);
            } else {
                this.selected = [];
            }
        },

        approveSelected() {
            this.approveDo(this.selected);
        },

        rejectSelected() {
            this.rejectDo(this.selected);
        },

        rejectDo(ids) {
            this.rejectRedemptions({ids: ids, history: false})
                .then(() => {
                    this.selected = [];
                })
                .catch(error => {
                    Bus.$emit('flash-message', {
                        type: 'error',
                        message: 'OOPS! Redemption approvals failed to load!'
                    });
                });
        },

        approveDo(ids) {
            this.approveRedemptions({ids: ids, history: false})
                .then(() => {
                    this.selected = [];
                })
                .catch(error => {
                    Bus.$emit('flash-message', {
                        type: 'error',
                        message: 'OOPS! Redemption approvals failed to load!'
                    });
                });
        },

        getApprovals(force = false, showMessage = false) {
            this.loadRedemptionApprovals(force)
                .then(() => {
                    if (showMessage) {
                        Bus.$emit('flash-message', {message: 'Redemptions reloaded!'});
                    }
                })
                .catch(error => {
                    Bus.$emit('flash-message', {
                        type: 'error',
                        message: 'OOPS! Redemption approvals failed to load!'
                    });
                });
        },

        panelistClicked(panelistId) {
            this.$router.push('/spa/panelistmanager/edit/' + panelistId);
        }
    },

    mounted() {
        this.getApprovals();
    },

    watch: {
        $route(to, from) {
            if (to.name === 'redemptions') {
                this.getApprovals(true,false);
            }
        }
    },

    created() {
        if (!this.$store.state[vuexModule]) {
            this.$store.registerModule(vuexModule, Store);
        }
    },
}

</script>
